import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { 
  User, GraduationCap, Briefcase, Award, Globe, 
  CheckCircle, Clock, AlertCircle, Calendar,
  Plus, Trash2, Save, Info, Building2, TrendingUp, ArrowRight, HelpCircle
} from 'lucide-react'
import { motion } from 'framer-motion'
import { Qualification, QualificationType } from '@/types/onboarding'
import QualificationForm from './QualificationForm'
import { InternalCareerHistory, InternalRoleEntry } from '@/services/api/profileService'

interface ProfileData {
  // From F&O (Read-only)
  currentRole: {
    jobTitle: string
    department: string
    campus: string
    lineManager: string
    employmentDate: string
  }
  
  // User-maintained - NQF-aligned qualifications
  qualifications: Qualification[]
  
  externalCareerHistory: {
    id: string
    organization: string
    position: string
    startDate: string
    endDate?: string
    directReports?: number
    extendedTeamSize?: number
    keyAchievements: string
    skillsDeveloped: string[]
  }[]
  
  professionalContext: {
    areasOfExpertise: string[]
    languageProficiencies: { language: string; proficiency: string }[]
    geographicExperience: string[]
    industryExperience: string[]
  }
  
  lastUpdated: string
  completionStatus: 'complete' | 'incomplete'
  nextUpdateDue: string
}

interface AnnualProfileManagerProps {
  profileData: ProfileData
  userRole: 'employee' | 'manager'
  updateTrigger?: 'annual' | 'role-change' | 'self-initiated' | 'manager-request'
  internalCareerHistory?: InternalCareerHistory | null
  onSaveProfile: (data: ProfileData) => void
  onCompleteProfile: (data: ProfileData) => void
}

// Pure CSS tooltip — hover-only, no sticky behavior
function FieldTooltip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex ml-1 group/tip cursor-help">
      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 group-hover/tip:text-muted-foreground transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-[260px] rounded-lg bg-gray-900 px-3.5 py-2.5 text-xs leading-relaxed text-gray-100 shadow-lg opacity-0 scale-95 transition-all duration-150 group-hover/tip:opacity-100 group-hover/tip:scale-100 z-50">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  )
}

export default function AnnualProfileManager({
  profileData,
  userRole,
  updateTrigger,
  internalCareerHistory,
  onSaveProfile,
  onCompleteProfile
}: AnnualProfileManagerProps) {
  const [localData, setLocalData] = useState<ProfileData>(profileData)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [newExpertise, setNewExpertise] = useState('')

  const daysUntilDue = Math.ceil((new Date(localData.nextUpdateDue).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
  const isOverdue = daysUntilDue < 0

  // Validate profile data before saving
  const validateProfile = (): string[] => {
    const errors: string[] = []
    
    // Validate qualifications
    localData.qualifications.forEach((q, index) => {
      if (q.type === 'professional') {
        // Professional certifications require Professional Body, not Institution
        if (!q.professionalBody?.trim()) {
          errors.push(`Qualification ${index + 1}: Professional Body is required for professional certifications`)
        }
      } else {
        // Academic and international qualifications require Institution
        if (!q.institution?.trim()) {
          errors.push(`Qualification ${index + 1}: Institution is required`)
        }
      }
    })
    
    // Validate career history
    localData.externalCareerHistory.forEach((c, index) => {
      if (!c.organization?.trim()) {
        errors.push(`Career History ${index + 1}: Organisation is required`)
      }
      if (!c.position?.trim()) {
        errors.push(`Career History ${index + 1}: Position is required`)
      }
      if (!c.startDate?.trim()) {
        errors.push(`Career History ${index + 1}: Start Date is required`)
      }
    })
    
    // Note: Language proficiencies form not yet implemented, skip validation
    
    return errors
  }

  const handleSave = () => {
    const errors = validateProfile()
    setValidationErrors(errors)
    if (errors.length === 0) {
      onSaveProfile(localData)
    }
  }

  const handleComplete = () => {
    const errors = validateProfile()
    setValidationErrors(errors)
    if (errors.length === 0) {
      onCompleteProfile(localData)
    }
  }

  const addQualification = (type: QualificationType = 'bachelors') => {
    setLocalData({
      ...localData,
      qualifications: [
        ...localData.qualifications,
        {
          id: Date.now().toString(),
          type: type,
          institution: '',
          country: type === 'international' ? 'international' : 'south_africa',
          inProgress: false
        }
      ]
    })
  }

  const updateQualification = (id: string, updatedQual: Qualification) => {
    setLocalData({
      ...localData,
      qualifications: localData.qualifications.map(q => q.id === id ? updatedQual : q)
    })
  }

  const addCareerHistory = () => {
    setLocalData({
      ...localData,
      externalCareerHistory: [
        ...localData.externalCareerHistory,
        {
          id: Date.now().toString(),
          organization: '',
          position: '',
          startDate: '',
          endDate: '',
          directReports: 0,
          extendedTeamSize: 0,
          keyAchievements: '',
          skillsDeveloped: []
        }
      ]
    })
  }

  const removeQualification = (id: string) => {
    setLocalData({
      ...localData,
      qualifications: localData.qualifications.filter(q => q.id !== id)
    })
  }

  const removeCareerHistory = (id: string) => {
    setLocalData({
      ...localData,
      externalCareerHistory: localData.externalCareerHistory.filter(h => h.id !== id)
    })
  }

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="bg-gradient-to-br from-heritage-blue to-purple-600 text-white rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2">Professional Profile</h2>
            <p className="text-white/90 mb-3">
              Keep your profile current — it helps your people leader prepare for meaningful career and development conversations with you.
            </p>
            <div className="flex items-center gap-3">
              {localData.completionStatus === 'complete' ? (
                <Badge className="bg-green-600 text-white border-0">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Profile Complete
                </Badge>
              ) : (
                <Badge className="bg-amber-600 text-white border-0">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Update Required
                </Badge>
              )}
              {updateTrigger && (
                <Badge variant="secondary" className="bg-white/20 text-white border-0">
                  Trigger: {updateTrigger.replace('-', ' ')}
                </Badge>
              )}
            </div>
          </div>
          {userRole === 'employee' && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handleSave}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Progress
              </Button>
              <Button
                onClick={handleComplete}
                className="bg-white hover:bg-heritage-blue text-heritage-blue hover:text-white"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Update
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Please fix the following errors:</h3>
              <ul className="text-sm text-red-800 mt-2 space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      )}

      {/* Update Reminder */}
      {isOverdue ? (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Profile Update Overdue</h3>
              <p className="text-sm text-red-800 mt-1">
                Your annual profile update was due {Math.abs(daysUntilDue)} days ago.
                Please complete your profile update before your development discussion.
              </p>
            </div>
          </div>
        </motion.div>
      ) : daysUntilDue <= 30 && daysUntilDue > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <h3 className="font-semibold text-amber-900">Profile Update Due Soon</h3>
              <p className="text-sm text-amber-800 mt-1">
                Your annual profile update is due in {daysUntilDue} days.
                Please update before your scheduled development and career discussions.
              </p>
            </div>
          </div>
        </motion.div>
      ) : null}

      {/* Current Role (From F&O - Read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Current Role (From F&O)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-600">Job Title</Label>
              <div className="font-semibold text-gray-900">{localData.currentRole.jobTitle}</div>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Function</Label>
              <div className="font-semibold text-gray-900">{localData.currentRole.department}</div>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Campus</Label>
              <div className="font-semibold text-gray-900">{localData.currentRole.campus}</div>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Line People Leader</Label>
              <div className="font-semibold text-gray-900">{localData.currentRole.lineManager}</div>
            </div>
          </div>
          <div className="mt-3 bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> Current role information is automatically synchronized from F&O.
              External career history is captured in the platform for development planning.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Qualifications - Enhanced with NQF Levels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Qualifications & Certifications
            </div>
            {userRole === 'employee' && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => addQualification('bachelors')} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Degree
                </Button>
                <Button onClick={() => addQualification('professional')} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-1" />
                  Professional Cert
                </Button>
                <Button onClick={() => addQualification('international')} size="sm" variant="outline">
                  <Plus className="h-4 w-4 mr-1" />
                  International
                </Button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* NQF Information Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800">
                Qualifications are aligned to the South African NQF (Levels 1-10). 
                International qualifications require SAQA evaluation for NQF equivalency.
              </p>
            </div>
          </div>

          {localData.qualifications.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <GraduationCap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No qualifications added yet</p>
              <p className="text-xs mt-1">Add your academic qualifications, professional certifications, or international credentials</p>
            </div>
          ) : (
            localData.qualifications.map((qual, index) => (
              <QualificationForm
                key={qual.id}
                qualification={qual}
                index={index}
                onChange={(updated) => updateQualification(qual.id, updated)}
                onRemove={() => removeQualification(qual.id)}
                disabled={userRole === 'manager'}
                showAdvancedFields={false}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Eduvos Career Journey (From F&O - Read-only) */}
      {internalCareerHistory && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-heritage-blue" />
                Eduvos Career Journey
              </div>
              {internalCareerHistory.tenure_years !== undefined && (
                <Badge variant="outline" className="bg-heritage-blue/10 text-heritage-blue border-heritage-blue/30">
                  {internalCareerHistory.tenure_years} years tenure
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Tenure Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-heritage-blue">
                  {internalCareerHistory.tenure_years?.toFixed(1) || '—'}
                </div>
                <div className="text-xs text-gray-600">Years at Eduvos</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-heritage-blue">
                  {internalCareerHistory.total_roles || 1}
                </div>
                <div className="text-xs text-gray-600">Roles Held</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-sm font-semibold text-heritage-blue">
                  {internalCareerHistory.original_hire_date 
                    ? new Date(internalCareerHistory.original_hire_date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })
                    : '—'
                  }
                </div>
                <div className="text-xs text-gray-600">Original Hire Date</div>
              </div>
            </div>

            {/* Current Role */}
            {internalCareerHistory.current_role && (
              <div className="border-l-4 border-heritage-blue bg-heritage-blue/5 rounded-r-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-green-100 text-green-800 text-xs">Current</Badge>
                  <span className="font-semibold">{internalCareerHistory.current_role.title}</span>
                </div>
                <div className="text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                  {internalCareerHistory.current_role.department && (
                    <span>{internalCareerHistory.current_role.department}</span>
                  )}
                  {internalCareerHistory.current_role.campus && (
                    <span>📍 {internalCareerHistory.current_role.campus}</span>
                  )}
                  {internalCareerHistory.current_role.start_date && (
                    <span>Since {new Date(internalCareerHistory.current_role.start_date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short' })}</span>
                  )}
                </div>
              </div>
            )}

            {/* Role History Timeline */}
            {internalCareerHistory.role_history && internalCareerHistory.role_history.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Career Progression at Eduvos
                </h4>
                <div className="space-y-2">
                  {internalCareerHistory.role_history
                    .filter(role => !role.is_current)
                    .map((role, index) => (
                      // One position can be held more than once (F&O history), so key by spell.
                      <div key={`${role.position_code}-${role.start_date ?? index}`} className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                        <div className="flex-1">
                          <span className="font-medium">{role.title}</span>
                          {role.department && <span className="text-gray-500"> • {role.department}</span>}
                        </div>
                        <div className="text-xs text-gray-500">
                          {role.start_date && new Date(role.start_date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short' })}
                          {role.end_date && ` – ${new Date(role.end_date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short' })}`}
                        </div>
                        {role.change_type && role.change_type !== 'initial' && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {role.change_type}
                          </Badge>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Empty state if no history */}
            {(!internalCareerHistory.role_history || internalCareerHistory.role_history.length === 0) && !internalCareerHistory.current_role && (
              <div className="text-center py-4 text-gray-500">
                <Building2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No internal career history available</p>
                <p className="text-xs">Position history will appear here once synced from F&O</p>
              </div>
            )}

            <div className="mt-3 bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Your Eduvos career journey is automatically synchronized from F&O.
                This includes promotions, transfers, and role changes within the organisation.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* External Career History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              External Career History
            </div>
            {userRole === 'employee' && (
              <Button onClick={addCareerHistory} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" />
                Add Position
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {localData.externalCareerHistory.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No external career history added yet</p>
            </div>
          ) : (
            localData.externalCareerHistory.map((history, index) => (
              <div key={history.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-sm">Position #{index + 1}</h4>
                  {userRole === 'employee' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeCareerHistory(history.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`hist-org-${history.id}`}>Organisation <span className="text-red-500">*</span></Label>
                      <Input
                        id={`hist-org-${history.id}`}
                        value={history.organization}
                        onChange={(e) => {
                          const newHistory = [...localData.externalCareerHistory]
                          newHistory[index].organization = e.target.value
                          setLocalData({ ...localData, externalCareerHistory: newHistory })
                        }}
                        disabled={userRole === 'manager'}
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`hist-pos-${history.id}`}>Position <span className="text-red-500">*</span></Label>
                      <Input
                        id={`hist-pos-${history.id}`}
                        value={history.position}
                        onChange={(e) => {
                          const newHistory = [...localData.externalCareerHistory]
                          newHistory[index].position = e.target.value
                          setLocalData({ ...localData, externalCareerHistory: newHistory })
                        }}
                        disabled={userRole === 'manager'}
                        placeholder="Job title"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                    <div>
                      <Label htmlFor={`hist-start-${history.id}`}>Start Date <span className="text-red-500">*</span></Label>
                      <Input
                        id={`hist-start-${history.id}`}
                        type="month"
                        value={history.startDate}
                        onChange={(e) => {
                          const newHistory = [...localData.externalCareerHistory]
                          newHistory[index].startDate = e.target.value
                          setLocalData({ ...localData, externalCareerHistory: newHistory })
                        }}
                        disabled={userRole === 'manager'}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`hist-end-${history.id}`}>End Date</Label>
                      <Input
                        id={`hist-end-${history.id}`}
                        type="month"
                        value={history.endDate || ''}
                        onChange={(e) => {
                          const newHistory = [...localData.externalCareerHistory]
                          newHistory[index].endDate = e.target.value
                          setLocalData({ ...localData, externalCareerHistory: newHistory })
                        }}
                        disabled={userRole === 'manager'}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`hist-reports-${history.id}`} className="flex items-center">
                        Direct Reports
                        <FieldTooltip text="People who reported directly to you in this role" />
                      </Label>
                      <Input
                        id={`hist-reports-${history.id}`}
                        type="number"
                        min="0"
                        value={history.directReports || 0}
                        onChange={(e) => {
                          const newHistory = [...localData.externalCareerHistory]
                          newHistory[index].directReports = parseInt(e.target.value) || 0
                          setLocalData({ ...localData, externalCareerHistory: newHistory })
                        }}
                        disabled={userRole === 'manager'}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`hist-team-${history.id}`} className="flex items-center">
                        Extended Team
                        <FieldTooltip text="Total team size including indirect reports — e.g. if you managed 3 team leads who each had 5 people, that's 18" />
                      </Label>
                      <Input
                        id={`hist-team-${history.id}`}
                        type="number"
                        min="0"
                        value={history.extendedTeamSize || 0}
                        onChange={(e) => {
                          const newHistory = [...localData.externalCareerHistory]
                          newHistory[index].extendedTeamSize = parseInt(e.target.value) || 0
                          setLocalData({ ...localData, externalCareerHistory: newHistory })
                        }}
                        disabled={userRole === 'manager'}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`hist-ach-${history.id}`}>Key Achievements</Label>
                    <Textarea
                      id={`hist-ach-${history.id}`}
                      value={history.keyAchievements}
                      onChange={(e) => {
                        const newHistory = [...localData.externalCareerHistory]
                        newHistory[index].keyAchievements = e.target.value
                        setLocalData({ ...localData, externalCareerHistory: newHistory })
                      }}
                      disabled={userRole === 'manager'}
                      rows={3}
                      placeholder="Notable accomplishments..."
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Professional Context */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Professional Context
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="expertise" className="flex items-center">
              Areas of Expertise
              <FieldTooltip text="Skills and knowledge domains where you have significant experience — e.g. curriculum design, data analytics, student counselling" />
            </Label>
            <div className="flex space-x-2 mt-2">
              <Input
                id="expertise"
                value={newExpertise}
                onChange={(e) => setNewExpertise(e.target.value)}
                placeholder="e.g., Machine Learning, Data Analytics"
                disabled={userRole === 'manager'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newExpertise.trim() && userRole !== 'manager') {
                    e.preventDefault()
                    setLocalData(prev => ({
                      ...prev,
                      professionalContext: {
                        ...prev.professionalContext,
                        areasOfExpertise: [...prev.professionalContext.areasOfExpertise, newExpertise.trim()]
                      }
                    }))
                    setNewExpertise('')
                  }
                }}
              />
              <Button
                type="button"
                size="icon"
                disabled={userRole === 'manager'}
                onClick={() => {
                  if (newExpertise.trim()) {
                    setLocalData(prev => ({
                      ...prev,
                      professionalContext: {
                        ...prev.professionalContext,
                        areasOfExpertise: [...prev.professionalContext.areasOfExpertise, newExpertise.trim()]
                      }
                    }))
                    setNewExpertise('')
                  }
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {localData.professionalContext.areasOfExpertise.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {localData.professionalContext.areasOfExpertise.map((area, idx) => (
                  <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1 px-3 py-1.5">
                    {area}
                    {userRole !== 'manager' && (
                      <button
                        type="button"
                        onClick={() => setLocalData(prev => ({
                          ...prev,
                          professionalContext: {
                            ...prev.professionalContext,
                            areasOfExpertise: prev.professionalContext.areasOfExpertise.filter((_, i) => i !== idx)
                          }
                        }))}
                        className="ml-1 hover:text-red-500"
                      >
                        ×
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manager View Note */}
      {userRole === 'manager' && (
        <Card className="border-purple-200 border-2">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-purple-900 text-sm mb-1">People Leader Profile Review</h4>
                <p className="text-xs text-purple-800">
                  Review team member profiles before career discussions and development planning.
                  Profile data informs succession planning, talent assessments, and development priorities.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
